import { z } from 'zod';

// Zod schema that matches the backend DTO requirements
export const accountingCenterSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  description: z.string().min(1, { message: 'Description is required' }),
  acmev: z.number().min(0, { message: 'ACMEV must be a positive number' }),
  acmev_alert_threshold: z.number().min(0, { message: 'ACMEV alert threshold must be a positive number' }),
  acrt: z.number().min(0, { message: 'ACRT must be a positive number' }),
  acrt_alert_threshold: z.number().min(0, { message: 'ACRT alert threshold must be a positive number' })
});

// Form schema for the frontend form
export const accountingCenterFormSchema = z.object({
  name: z.string().min(1, { message: 'Name is required' }),
  description: z.string().min(1, { message: 'Description is required' }),
  acmev: z.union([
    z.number().min(0, { message: 'ACMEV must be a positive number' }),
    z
      .string()
      .min(1, { message: 'ACMEV is required' })
      .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
        message: 'ACMEV must be a positive number'
      })
  ]),
  acmev_alert_threshold: z.union([
    z.number().min(0, { message: 'ACMEV alert threshold must be a positive number' }),
    z
      .string()
      .min(1, { message: 'ACMEV alert threshold is required' })
      .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
        message: 'ACMEV alert threshold must be a positive number'
      })
  ]),
  acrt: z.union([
    z.number().min(0, { message: 'ACRT must be a positive number' }),
    z
      .string()
      .min(1, { message: 'ACRT is required' })
      .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
        message: 'ACRT must be a positive number'
      })
  ]),
  acrt_alert_threshold: z.union([
    z.number().min(0, { message: 'ACRT alert threshold must be a positive number' }),
    z
      .string()
      .min(1, { message: 'ACRT alert threshold is required' })
      .refine((val) => !isNaN(Number(val)) && Number(val) >= 0, {
        message: 'ACRT alert threshold must be a positive number'
      })
  ])
});

export type AccountingCenterFormValues = z.infer<typeof accountingCenterFormSchema>;

export interface AccountingCenter {
  _id: string;
  name: string;
  description: string;
  type: string;
  acmev: number;
  acmev_alert_threshold: number;
  acrt: number;
  acrt_alert_threshold: number;
  budgetId: string;
  [key: string]: any;
}

// Request Data interfaces
export type AccountingCenterCreationData = z.infer<typeof accountingCenterSchema>;

export interface AccountingCenterCreationResponse {
  success: boolean;
  message: string;
  data: AccountingCenter;
}

export type AccountingCenterUpdateData = Partial<AccountingCenterCreationData>;

export interface AccountingCenterUpdateResponse {
  success: boolean;
  message: string;
  data: AccountingCenter;
}

export interface AccountingCenterResponse {
  success: boolean;
  message: string;
  data: AccountingCenter;
}

export interface AccountingCentersResponse {
  success: boolean;
  message: string;
  data: AccountingCenter[];
}
